// import node module libraries
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Col, Row, Breadcrumb, Card, Dropdown, Form, Button, Spinner, Offcanvas, OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import { Eye, EyeOff, MoreVertical, Edit, Trash, Plus, Clipboard } from 'react-feather';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// import custom components
import { FormSelect } from 'components/elements/form-select/FormSelect';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import ModalDeleteAPICredential from './ModalDeleteAPICredential';
import ModalShowNewAPICredential from './ModalShowNewAPICredential';
import OffcanvasUpdateAPICredential from './OffcanvasUpdateAPICredential';
import OffcanvasCreateAPICredential from './OffcanvasCreateAPICredential';

const APICredentials = () => {
	const { t, i18n } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [selectedRow, setSelectedRow] = useState({});
	
	const [isLoading, setIsLoading] = useState(null); // State for loading table

	const [showAlertAccountActivation, setShowAlertAccountActivation] = useState(null); // State for showing alert
	const [showChangePasswordOffcanvas, setShowChangePasswordOffcanvas] = useState(false);
	const handleShowChangePassword = () => setShowChangePasswordOffcanvas(true);
	const handleCloseChangePassword = () => setShowChangePasswordOffcanvas(false);
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const [apiCredentials, setAPICredentials] = useState([]); // State for API Credentials

	const handlePasswordToggle = () => {
		setShowPassword(!showPassword);
		setTimeout(() => {
			setShowPassword(false);
		}, 10000);
	};

	const auth = useAuthUser(); // Assuming this gets the auth user data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async (page) => {
		setIsLoading(true);
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Get APIs
		await axios.get(`${baseURL}/auth/apis/`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setAPICredentials(responseData.sort((a, b) => new Date(a.api_timestamp_created) - new Date(b.api_timestamp_created)));
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	useEffect(() => {
		getData();
	}, []);

	useEffect(() => {
		if(auth && auth.is_active === 0) {
			setShowAlertAccountActivation(true);
		}
	}, []);

	const fetchRowDetails = async (rowId) => {
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		try {
			const response = await axios.get(`${baseURL}/auth/apis/${rowId}`, options);
			const rowDetails = response.data;
			setSelectedRow(rowDetails.data); // Update selectedRow state with fetched log details

		} catch (error) {
			console.error(error);
		}
	};

	// Create FAQ
	const [isLoadingButtonCreateAPI, setIsLoadingButtonCreateAPI] = useState(null); // State for loading spinner
	const [showCreateAPIOffcanvas, setShowCreateAPIOffcanvas] = useState(false);
	const handleShowCreateAPI = () => setShowCreateAPIOffcanvas(true);
	const handleCloseCreateAPI = () => setShowCreateAPIOffcanvas(false);
	const [createdNewAPI, setCreatedNewAPI] = useState(null); // State to trigger re-fetch
	const [showNewAPIModal, setShowNewAPIModal] = useState(false); // State for showing modal

	const handleCreateAPI = async (e) => {
		e.preventDefault();
		setIsLoadingButtonCreateAPI(true); // Set loading to true

		const data = {
			tenant_id: auth.tenant_id,
			user_id: auth.user_id,
			api_description: e.target.api_description.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/auth/apis/`, data, options)
			.then(response => {
				const responseData = response.data.result;
				setAPICredentials([...apiCredentials, responseData]);
				// Close the offcanvas
				setShowCreateAPIOffcanvas(false);
				setIsLoadingButtonCreateAPI(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				// Show new created API Credential Modal
				setCreatedNewAPI(responseData);
				setShowNewAPIModal(true);
			})
			.catch((error) => {
				// Show error notification
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsLoadingButtonCreateAPI(false); // Stop loading
			});
	};

	// Update API Credentials
	const [isLoadingButtonUpdateAPI, setIsLoadingButtonUpdateAPI] = useState(null); // State for loading spinner
	const [showUpdateAPIOffcanvas, setShowUpdateAPIOffcanvas] = useState(false);
	const handleShowUpdateAPI = () => setShowUpdateAPIOffcanvas(true);
	const handleCloseUpdateAPI = () => {
		setShowUpdateAPIOffcanvas(false);
		setSelectedRow({});
	};

	const handleUpdateAPI = async (e) => {
		e.preventDefault();
		setIsLoadingButtonUpdateAPI(true);

		const data = {
			api_description: e.target.api_description.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/auth/apis/${e.target.id.value}`, data, options)
			.then(response => {
				// Update the selected faq in the data state
				const updatedRow = response.data;
				setSelectedRow(updatedRow);
				// Update the apidata state with the updated row
				const updatedData = apiCredentials.map((row) => 
					row.id === updatedRow.result.id 
						? { ...row, api_description: updatedRow.result.api_description } 
						: row
				);
				setAPICredentials(updatedData);
				// Update the faqdata state with the updated data
				// Close the offcanvas
				setShowUpdateAPIOffcanvas(false);
				setIsLoadingButtonUpdateAPI(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setIsLoadingButtonUpdateAPI(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// Delete API Credentials
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [apiCredentialToDelete, setApiCredentialToDelete] = useState(null); // State for API Credential to delete
	const [isLoadingButtonDeleteAPICredential, setIsLoadingButtonDeleteAPICredential] = useState(null); // State for loading spinner

	const handleDeleteClick = (credential) => {
		setApiCredentialToDelete(credential);
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		if (apiCredentialToDelete) {
			setIsLoadingButtonDeleteAPICredential(true);
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			await axios.delete(`${baseURL}/auth/apis/${apiCredentialToDelete.id}`, options)
				.then(response => {
					setShowDeleteModal(false);
					setAPICredentials(apiCredentials.filter(credential => credential.id !== apiCredentialToDelete.id));
					setIsLoadingButtonDeleteAPICredential(false);
					handleShowNotification(setNotification, 'success', 'Erfolgreich', t(response.data.message));
				})
				.catch((error) => {
					setShowDeleteModal(false);
					setIsLoadingButtonDeleteAPICredential(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				});
		}
	};

	const [isButtonActivationDisabled, setIsButtonActivationDisabled] = useState(false);

	const handleSendActivationLink = async () => {
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
			params: {
			},
			headers: {
				Authorization: authHeader,
				Accept: 'application/json'
			}
		};
		const data = {
			username: auth.username,
			otp_valid_for: "activate_account"
		}

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/auth/generate-otp`, data, options)
			.then(response => {
				setIsButtonActivationDisabled(false);
				setIsLoading(false); // Stop loading
				handleShowNotification(setNotification, 'info', 'Information', response.data.message + '. Bitte überprüfe dein Postfach und folge den Anweisungen in der E-Mail.');

			})
			.catch((error) => {
				setIsButtonActivationDisabled(false);
				setIsLoading(false); // Stop loading
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Deine Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut oder kontaktiere den Support über hello@haeppy-shopping.com.');
			})
	};

	// Methods / Functions
	const handleRowClick = (row, action) => {
		if (action === 'open') {
			setShowUpdateAPIOffcanvas(true);
			fetchRowDetails(row.id); // Call API to fetch log details
		} else if (action === 'delete') {
			handleDeleteClick(row);
			setShowUpdateAPIOffcanvas(false);
		}
	};

	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	// 
	// Components
	//

	// Component ActionMenu
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.ID, 'open')}>
						<Edit size="14px" className="me-1" /> {t('Edit')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.ID, 'delete')}>
						<Trash size="14px" className="me-1" /> {t('Delete')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				{/* Create API */}
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<div className="border-bottom pb-4 mb-4 d-flex align-items-center justify-content-end">
						<Button variant="secondary" className="me-2" onClick={handleShowCreateAPI}>
							{t('Create new REST-API Credentials')}
						</Button>
					</div>
				</Col>
			</Row>
			<Row>
				<Col>
					<Row>
						{/* notifaction user activation */}
						{showAlertAccountActivation && (
						<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
							<Alert variant="warning" className='bg-light-warning text-dark-warning p-4' onClose={() => setShowAlertAccountActivation(false)} dismissible>
								<Alert.Heading className="mb-2">
									<strong>{t('Warning')}</strong>
								</Alert.Heading>
								{t('Your Account is not activated')}.{' '}{t('Please check your Mailbox for Activation Mail or')}{' '}
								<Button variant="outline-dark-warning rounded ms-2" size="sm" onClick={handleSendActivationLink} disabled={isButtonActivationDisabled}>
									{isLoading ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" />
											{t('Resend Activation Link')}
										</>
									) : (
										t('Resend Activation Link')
									)}
								</Button>
							</Alert>
						</Col>
						)}
						{/* end of notifaction user activation */}
						{/* start of api-credentials */}
						<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
							<Card className="mb-4">
								<Card.Header className="d-flex align-items-center justify-content-between">
									<h4 className="mb-0">{t('REST-API Credentials')}</h4>
								</Card.Header>
								<Card.Body>
									<Row className="">
										<Col lg={12} md={12} className="">
											<p>{t('REST-API Credentials are used to authenticate your application with the REST-API.')} {t('You can create multiple REST-API Credentials for different applications.')}</p>
											<p></p>
										</Col>
									</Row>
									<Table size="sm">
										<thead>
											<tr>
												<th>{t('Application Name')}</th>
												<th>{t('Client ID')}</th>
												<th>{t('Timestamp created')}</th>
												<th>{t('Action')}</th>
											</tr>
										</thead>
										<tbody>
											{apiCredentials.map((credential) => (
												<tr key={credential.id}>
													<td>
														<Link className="fw-semibold" onClick={(event) => {
															event.preventDefault();
															handleRowClick(credential, 'open');
														}}>
															{credential.api_description || ''} <i className="fe fe-arrow-right fs-4"></i>
														</Link>	
													</td>
													<td>
														{credential.api_client_id || ''}
													</td>
													<td>
														{credential.api_timestamp_created || ''}
													</td>
													<td>
														<ActionMenu ID={credential} />
													</td>
												</tr>
											))}
										</tbody>
									</Table>
								</Card.Body>
							</Card>
						</Col>
						{/* end of api-credentials */}
					</Row>
				</Col>
			</Row>
			{/* Create API Credential Offcanvas */}
			<OffcanvasCreateAPICredential
				show={showCreateAPIOffcanvas}
				onHide={handleCloseCreateAPI}
				onSubmit={handleCreateAPI}
				isLoadingButton={isLoadingButtonCreateAPI}
			/>
			{/* Update API Credential Offcanvas */}
			{selectedRow && Object.keys(selectedRow).length > 0 && (
				<OffcanvasUpdateAPICredential
					show={showUpdateAPIOffcanvas}
					onHide={handleCloseUpdateAPI}
					selectedRow={selectedRow}
					onSubmit={handleUpdateAPI}
					isLoadingButton={isLoadingButtonUpdateAPI}
				/>
			)}
			{/* Show new created API Credential Modal */}
			{createdNewAPI && Object.keys(createdNewAPI).length > 0 && (
				<ModalShowNewAPICredential
					show={showNewAPIModal}
					onHide={() => {
						setShowNewAPIModal(false);
						setCreatedNewAPI(null);
					}}
					createdCredential={createdNewAPI}
				/>
			)}
			{/* Delete API Credential Modal */}
			<ModalDeleteAPICredential
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteAPICredential}
			/>
		</Fragment>
	);
};

export default APICredentials;
